import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { initiateBuy } from "../../api/apiServices";
import { useAssets } from "../../hooks/useAssets";
import useAuthStatus from "../../hooks/useAuthStatus";
import { useTradingConfig } from "../../hooks/useTradingConfig";
import ActionGate from "../../components/feature/ActionGate";
import PageLoader from "../../components/PageLoader";
import { useServices } from "../../hooks/useServices";
import ServiceGate from "../../components/feature/ServiceGate";

const fmt = (n) => Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 });

const NETWORK_LABELS = {
  bsc:  "BNB Smart Chain (BEP20)",
  eth:  "Ethereum (ERC20)",
  ton:  "TON Network",
  sol:  "Solana",
  tron: "TRON (TRC20)",
};
const netLabel = (n) => NETWORK_LABELS[n?.toLowerCase()] || n?.toUpperCase() || n;

const QUICK_AMOUNTS = [5000, 10000, 25000, 50000, 100000];

export default function BuyCrypto() {
  const { services } = useServices();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [step,            setStep]            = useState("select");
  const [selectedAsset,   setSelectedAsset]   = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [nairaAmount,     setNairaAmount]     = useState("");

  // ── Assets from hook (already polling) ───────────────────────
  const { assets, loading } = useAssets({ pollInterval: 60_000 });

  const { fees, limits, config, loading: configLoading } = useTradingConfig({ enabled: isAuthenticated });

  // Reset network when asset changes
  const handleSelectAsset = (asset) => {
    setSelectedAsset(asset);
    // Auto-select if only one network, else clear so user must pick
    setSelectedNetwork(asset.networks.length === 1 ? asset.networks[0].network : null);
    setStep("amount");
  };

  // ── Derived ───────────────────────────────────────────────────
  const priceNGN = selectedAsset?.priceNgn || 0;
  const cryptoAmount = nairaAmount && priceNGN
    ? (parseFloat(nairaAmount) / priceNGN).toFixed(6)
    : "0.000000";

   // Use the real fee from config instead of hardcoding 1.5%
  const feePercent = fees.buyFeePercent || 1.5;
  const fee        = nairaAmount ? (parseFloat(nairaAmount) * feePercent / 100).toFixed(2) : "0.00";
  const total      = nairaAmount ? (parseFloat(nairaAmount) * (1 + feePercent / 100)).toFixed(2) : "0.00";

  // Minimum buy from config
  const minBuy = limits.minBuyNGN || 1000;

  const canProceedToConfirm = nairaAmount &&
    parseFloat(nairaAmount) > minBuy &&
    selectedNetwork;

  // ── Confirm & pay ─────────────────────────────────────────────
  const handleConfirm = async () => {
    setStep("processing");
    try {
      const data = await initiateBuy(
        selectedAsset.symbol,
        parseFloat(nairaAmount),
        selectedNetwork,          // ← passed to backend
      );
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (err) {
      console.error(err);
      setStep("confirm");
    }
  };

  const goBack = () => {
    if (step === "select" || step === "amount") navigate("/home");
    else if (step === "confirm") setStep("amount");
    else setStep("select");
  };

  

  if (loading) return <PageLoader size="md" message="Buy crypto loading..." />

  return (
    <ServiceGate serviceKey="trading" services={services} fullPage>
      <div className="min-h-screen bg-[#F7F8FA] pb-28">
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-4 flex items-center gap-3 border-b border-slate-100">
        <button onClick={goBack}
          className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200">
          <i className="ri-arrow-left-line text-slate-600 text-lg" />
        </button>
        <h1 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Buy Crypto
        </h1>
      </div>

      <div className="px-5 py-4">
        {/* Progress bar */}
        <div className="flex gap-1.5 mb-6">
          {["Select Asset", "Enter Amount", "Confirm"].map((s, i) => {
            const idx = step === "select" ? 0 : step === "amount" ? 1 : 2;
            return (
              <div key={s} className="flex-1 h-1.5 rounded-full transition-all"
                style={{ background: i <= idx ? "#059669" : "#E2E8F0" }} />
            );
          })}
        </div>

        {/* ── STEP 1: Select asset ── */}
        {step === "select" && (
          <div>
            <p className="text-slate-500 text-sm mb-4">Choose the crypto you want to buy with Naira</p>
            <div className="space-y-2">
              {assets.map(asset => (
                <button key={asset.symbol} onClick={() => handleSelectAsset(asset)}
                  className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-4 flex items-center justify-between hover:border-emerald-300 hover:bg-emerald-50/30 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden bg-slate-100">
                      {asset.image
                        ? <img src={asset.image} alt={asset.symbol} className="w-7 h-7 object-contain" />
                        : <i className="ri-coin-line text-slate-400" />}
                    </div>
                    <div className="text-left">
                      <p className="text-slate-800 text-sm font-bold">{asset.symbol}</p>
                      <p className="text-slate-400 text-xs">
                        {asset.networks.length === 1
                          ? netLabel(asset.networks[0].network)
                          : `${asset.networks.length} networks`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-800 text-sm font-semibold">₦{fmt(asset.priceNgn)}</p>
                    <p className={`text-xs font-semibold ${asset.changeType === "positive" ? "text-emerald-600" : "text-red-500"}`}>
                      {asset.change24h >= 0 ? "+" : ""}{asset.change24h.toFixed(2)}%
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 2: Amount + network ── */}
        {step === "amount" && selectedAsset && (
          <div>
            {/* Asset chip */}
            <div className="flex items-center gap-2 mb-5 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 w-fit">
              {selectedAsset.image && <img src={selectedAsset.image} alt={selectedAsset.symbol} className="w-5 h-5 object-contain" />}
              <span className="text-slate-700 text-sm font-semibold">{selectedAsset.name || selectedAsset.symbol}</span>
              <button onClick={() => setStep("select")} className="text-emerald-600 text-xs ml-1 font-medium">Change</button>
            </div>

            {/* ── Network selector — only shown for multi-network assets ── */}
            {selectedAsset.networks.length > 1 && (
              <div className="mb-5">
                <p className="text-slate-600 text-xs font-medium mb-2">
                  Select network <span className="text-red-400">*</span>
                </p>
                <div className="flex gap-2 flex-wrap">
                  {selectedAsset.networks.map(n => (
                    <button key={n.network} onClick={() => setSelectedNetwork(n.network)}
                      className="px-4 py-2.5 rounded-xl border text-xs font-bold transition-all"
                      style={{
                        background:  selectedNetwork === n.network ? "#05966915" : "#fff",
                        borderColor: selectedNetwork === n.network ? "#059669"   : "#E2E8F0",
                        color:       selectedNetwork === n.network ? "#059669"   : "#94A3B8",
                      }}>
                      {netLabel(n.network)}
                    </button>
                  ))}
                </div>
                {!selectedNetwork && (
                  <p className="text-amber-500 text-xs mt-1.5 flex items-center gap-1">
                    <i className="ri-information-line" /> Please select a network to continue
                  </p>
                )}
              </div>
            )}

            {/* You Pay */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-3">
              <p className="text-slate-400 text-xs mb-2 font-medium">You Pay (NGN)</p>
              <div className="flex items-center gap-2">
                <span className="text-emerald-600 text-2xl font-bold">₦</span>
                <input type="number" value={nairaAmount} onChange={e => setNairaAmount(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 bg-transparent text-slate-800 text-3xl font-bold placeholder-slate-200 focus:outline-none"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }} />
              </div>
            </div>

            {/* Quick amounts */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {QUICK_AMOUNTS.map(amt => (
                <button key={amt} onClick={() => setNairaAmount(String(amt))}
                  className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs whitespace-nowrap hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
                  ₦{amt.toLocaleString()}
                </button>
              ))}
            </div>

            {/* You Receive */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4">
              <p className="text-slate-400 text-xs mb-2 font-medium">You Receive ({selectedAsset.symbol})</p>
              <div className="flex items-center gap-2">
                <span className="text-slate-800 text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {cryptoAmount}
                </span>
                <span className="text-slate-400 text-base">{selectedAsset.symbol}</span>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 space-y-2 mb-6">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Rate</span>
                <span className="text-slate-600">1 {selectedAsset.symbol} = ₦{fmt(priceNGN)}</span>
              </div>
              {selectedNetwork && (
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Network</span>
                  <span className="text-slate-600">{netLabel(selectedNetwork)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Processing fee ({feePercent}%)</span>
                <span className="text-slate-600">₦{fmt(fee)}</span>
              </div>
              <div className="flex justify-between text-xs border-t border-slate-200 pt-2">
                <span className="text-slate-800 font-semibold">Total Debit</span>
                <span className="text-emerald-600 font-bold">₦{fmt(total)}</span>
              </div>
            </div>
              <button
              onClick={() => canProceedToConfirm ? setStep("confirm") : undefined}
              disabled={!canProceedToConfirm}
              className="w-full py-4 rounded-2xl text-white font-semibold text-base transition-all active:scale-95 disabled:opacity-40 bg-emerald-600 hover:bg-emerald-700">
                Continue to Payment
              </button>
            <p className="text-center text-slate-400 text-xs mt-2">
              {!selectedNetwork && selectedAsset.networks.length > 1
                ? "Select a network above to continue"
                : `Minimum: ₦${minBuy}`}
            </p>
          </div>
        )}

        {/* ── STEP 3: Confirm ── */}
        {step === "confirm" && (
          <div>
            <h2 className="text-slate-800 text-xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Confirm Order
            </h2>
            <p className="text-slate-400 text-sm mb-6">Review your order before paying</p>

            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 mb-5">
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">Asset</span>
                <span className="text-slate-800 text-sm font-bold">{selectedAsset.name || selectedAsset.symbol} ({selectedAsset.symbol})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">Network</span>
                <span className="text-slate-800 text-sm font-bold">{netLabel(selectedNetwork)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">You Pay</span>
                <span className="text-slate-800 text-sm font-bold">₦{fmt(nairaAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">Fee (1.5%)</span>
                <span className="text-slate-600 text-sm">₦{fmt(fee)}</span>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-3">
                <span className="text-slate-800 text-sm font-bold">Total Charged</span>
                <span className="text-emerald-600 text-sm font-bold">₦{fmt(total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">You Receive</span>
                <span className="text-emerald-600 text-sm font-bold">{cryptoAmount} {selectedAsset.symbol}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">Payment Method</span>
                <span className="text-slate-700 text-sm flex items-center gap-1">
                  <i className="ri-bank-card-line text-emerald-600" /> Card/Transfer
                </span>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
              <i className="ri-shield-check-line text-emerald-600 text-lg mt-0.5" />
              <p className="text-slate-500 text-xs leading-relaxed">
                Your payment is processed securely via Paystack. Once confirmed, {selectedAsset.symbol}{" "}
                will be credited to your <strong>{netLabel(selectedNetwork)}</strong> wallet within 2 minutes.
              </p>
            </div>

            <button onClick={handleConfirm}
              className="w-full py-4 rounded-2xl text-white font-semibold text-base transition-all active:scale-95 bg-emerald-600 hover:bg-emerald-700">
              <i className="ri-secure-payment-line mr-2" />
              Pay ₦{fmt(total)} via Card/Transfer
            </button>
          </div>
        )}

        {/* ── Processing ── */}
        {step === "processing" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
              <i className="ri-loader-4-line text-emerald-600 text-4xl animate-spin" />
            </div>
            <h2 className="text-slate-800 text-xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Processing Payment
            </h2>
            <p className="text-slate-400 text-sm">
              Redirecting to Paystack to pay for your {selectedAsset?.symbol}…
            </p>
          </div>
        )}

        {/* ── Success ── */}
        {step === "success" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 border-2 border-emerald-200 flex items-center justify-center mb-6">
              <i className="ri-check-line text-emerald-600 text-4xl" />
            </div>
            <h2 className="text-slate-800 text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Order Successful!
            </h2>
            <p className="text-slate-400 text-sm mb-4">Payment confirmed. Your crypto has been credited.</p>
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-6 py-4 mb-8 w-full max-w-xs">
              <p className="text-slate-400 text-xs mb-1">You received</p>
              <p className="text-emerald-600 text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {cryptoAmount} {selectedAsset?.symbol}
              </p>
              <p className="text-slate-400 text-xs mt-1">on {netLabel(selectedNetwork)}</p>
            </div>
            <button onClick={() => navigate("/home")}
              className="w-full max-w-xs py-4 rounded-2xl text-white font-semibold bg-emerald-600 hover:bg-emerald-700">
              Back to Home
            </button>
            <button onClick={() => navigate("/transactions")} className="mt-3 text-emerald-600 text-sm font-medium">
              View Transaction
            </button>
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
    </ServiceGate>
    
  );
}
